import React from "react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Skeleton } from "@/components/ui/skeleton";
import { Badge } from "@/components/ui/badge";
import type {
    BadgeZoneViolationIncident,
    PedestrianDevice,
    StationaryUnit,
    User,
    Vehicle,
} from "../../../api";
import { store } from "../../../store";
import { useTranslation } from "react-i18next";

export type EntityCardProps = React.PropsWithChildren &
    (
        | {
              user?: User;
              unknown: boolean;
              vehicle?: never;
              stationaryUnit?: never;
              badgeZoneViolationIncident?: never;
              pedestrianDevice?: never;
          }
        | {
              user?: never;
              unknown?: never;
              vehicle?: never;
              stationaryUnit?: never;
              badgeZoneViolationIncident?: BadgeZoneViolationIncident;
              pedestrianDevice?: PedestrianDevice;
          }
        | {
              user?: never;
              unknown?: never;
              vehicle?: Vehicle;
              stationaryUnit?: never;
              badgeZoneViolationIncident?: never;
              pedestrianDevice?: never;
          }
        | {
              user?: never;
              unknown?: never;
              vehicle?: never;
              stationaryUnit?: StationaryUnit;
              badgeZoneViolationIncident?: never;
              pedestrianDevice?: never;
          }
    ) & { loading?: boolean; badgeLabel?: string; image?: string };

export const EntityCard = ({
    user,
    unknown,
    vehicle,
    loading,
    badgeLabel,
    image,
    stationaryUnit,
    badgeZoneViolationIncident,
    pedestrianDevice,
}: EntityCardProps): JSX.Element => {
    const { t } = useTranslation();

    if (loading) {
        return (
            <div className="flex items-center space-x-4">
                <Skeleton className="h-10 w-10 rounded-full" />
                <div className="space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-24" />
                </div>
            </div>
        );
    }

    if (!user && unknown === false) {
        return <>{t("component.operatorDetails.notLoggedIn")}</>;
    }

    return (
        <div className="flex justify-between">
            <div className="flex gap-4">
                <Avatar>
                    <AvatarImage
                        src={user?.imageId ? store.serviceFile.getFileUri(user.imageId) : image}
                        alt="avatar"
                    />
                    <AvatarFallback>
                        {user &&
                            `${user.firstName.charAt(0).toUpperCase()}${user.lastName.charAt(0).toUpperCase()}`}
                        {!user && unknown && "KR"}
                        {vehicle && "T"}
                        {stationaryUnit && "S"}
                    </AvatarFallback>
                </Avatar>
                <div className="flex flex-col gap-1">
                    <div className="font-semibold">
                        {user && `${user.firstName} ${user.lastName}`}
                        {badgeZoneViolationIncident && t("component.badgeDetails.pedestrianModule")}
                        {vehicle && vehicle.manufacturer}{" "}
                        {vehicle &&
                            (vehicle.model ?? t("component.vehicleDetails.unspecifiedModel"))}
                        {stationaryUnit &&
                            t(
                                `component.stationaryUnitDetails.${stationaryUnit.stationaryUnitType}`,
                            )}
                    </div>
                    <div className="text-muted-foreground">
                        {user && user.employeeId}
                        {pedestrianDevice && `${pedestrianDevice.networkId}`}
                        {vehicle &&
                            (vehicle.serialNumber ?? t("component.vehicleDetails.notConfigured"))}
                        {stationaryUnit && stationaryUnit.serialNumber}
                    </div>
                </div>
            </div>
            {loading && <Skeleton className="h-6 w-16" />}
            {!loading && badgeLabel && <Badge className="h-fit">{badgeLabel}</Badge>}
        </div>
    );
};
